import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { scrollToTop } from '../utils/scrollUtils';
import {
  formControlClass,
  formErrorBannerClass,
  formLabelClass,
  formPrimaryButtonClass,
  pageCanvasClass,
  surfaceCardClass,
} from '../components/ui/formUiClasses';

const Login = () => {
  const [formData, setFormData] = useState({
    identifier: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  const safeReturnTo = () => {
    const raw = searchParams.get('returnTo');
    if (!raw || !raw.startsWith('/') || raw.startsWith('//')) return '/';
    return raw;
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    if (error) {
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationErrors = [];
    if (!formData.identifier.trim()) {
      validationErrors.push(t('common.enterUsernameOrEmail'));
    }
    if (!formData.password) {
      validationErrors.push(t('common.passwordRequired'));
    }

    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      return;
    }

    setLoading(true);

    try {
      await login(formData, t);
      scrollToTop({ behavior: 'instant' });
      navigate(safeReturnTo(), { replace: true, state: { resetScroll: true } });
    } catch (err) {
      // Handle specific error types with translated messages
      let errorMessage = t('auth.login.failed');

      if (err.message) {
        if (err.message.includes('Invalid credentials')) {
          errorMessage = t('auth.login.invalidCredentials');
        } else if (err.message.includes('too many')) {
          errorMessage = t('auth.login.tooManyAttempts');
        } else if (err.message.includes('locked')) {
          errorMessage = t('auth.login.accountLocked');
        } else if (err.message.includes('server') || err.message.includes('500')) {
          errorMessage = t('auth.login.serverError');
        } else if (err.message.includes('network') || err.message.includes('fetch')) {
          errorMessage = t('auth.login.networkError');
        } else {
          // Keep original server message if it's specific and meaningful
          errorMessage = err.message;
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`h-full flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 ${pageCanvasClass}`}>
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-10 text-center text-3xl font-extrabold text-ink">
            {t('auth.login.title')}
          </h2>
        </div>
        <form className={`mt-8 space-y-6 p-8 shadow-md ${surfaceCardClass}`} onSubmit={handleSubmit} noValidate>
          <div className="space-y-4">
            <div>
              <label htmlFor="identifier" className={`${formLabelClass} mb-1`}>
                {t('common.username')} / {t('common.email')}
              </label>
              <input
                id="identifier"
                name="identifier"
                type="text"
                required
                className={formControlClass}
                placeholder={t('common.enterUsernameOrEmail')}
                value={formData.identifier}
                onChange={handleChange}
              />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label htmlFor="password" className={formLabelClass}>
                  {t('common.password')}
                </label>
                <Link to="/forgot-password" className="text-sm font-medium text-link hover:text-link-hover">
                  {t('common.forgotPassword')}
                </Link>
              </div>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  className={`${formControlClass} pr-10`}
                  placeholder={t('common.enterPassword')}
                  value={formData.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((visible) => !visible)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-subtle hover:text-ink"
                  aria-label={showPassword ? t('common.hidePassword') : t('common.showPassword')}
                  title={showPassword ? t('common.hidePassword') : t('common.showPassword')}
                >
                  {showPassword ? <FaEyeSlash className="h-4 w-4" aria-hidden /> : <FaEye className="h-4 w-4" aria-hidden />}
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className={formErrorBannerClass} role="alert">
              {error}
            </div>
          )}

          <div>
            <button
              type="submit"
              disabled={loading}
              className={`w-full ${formPrimaryButtonClass}`}
            >
              {loading ? t('common.loading') : t('common.login')}
            </button>
          </div>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-edge-strong" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-surface text-ink-subtle">
                {t('auth.login.noAccount')} <Link to={`/register?returnTo=${encodeURIComponent(safeReturnTo())}`} className="font-medium text-link hover:text-link-hover">{t('auth.login.signUp')}</Link>
              </span>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login; 
